import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import { message } from 'antd';
import { MagnifyingGlassIcon, PlusIcon, ChevronRightIcon } from '@heroicons/react/24/outline';
import releaseNoteAdminApi from '../../services/releaseNoteAdminApi';
import {
  PageHeader,
  TWInput,
  TWButton,
  TWDataTable,
  StatusBadge,
  SegmentedToggle,
  EmptyState,
} from '../../components/ui';
import { headerCls } from '../../data/columns';
import {
  releaseNoteStatusMap,
  formatPublishedAt,
  publisherName,
  apiErrorText,
  isForbidden,
  ADMIN_PATH,
} from '../release-notes/release-note-model';
import useCanManageReleaseNotes from './use-can-manage';
import AccessDenied, { ADMIN_SHELL_CLASS } from './components/AccessDenied';

const ADMIN_PAGE_SIZE = 100; // the API's maximum; one page holds every note for a long time
const SEARCH_DEBOUNCE_MS = 400;

const columns = [
  {
    // Fixed width: TWDataTable scrolls at max-content, so an unconstrained
    // title would stretch to one 600px line and `truncate` would be a no-op.
    title: <span className={headerCls}>Title</span>,
    key: 'title',
    width: 320,
    render: (_, r) => (
      <div className="flex max-w-[20rem] min-w-0 flex-col gap-0.5 whitespace-normal">
        <span className="font-semibold text-slate-900 dark:text-slate-100">{r.title}</span>
        <span className="block max-w-[20rem] truncate text-xs text-slate-500 dark:text-slate-400">/release-notes/{r.slug}</span>
      </div>
    ),
  },
  {
    title: <span className={headerCls}>Status</span>,
    key: 'status',
    render: (_, r) => <StatusBadge map={releaseNoteStatusMap} status={r.status} size="sm" dot={r.status === 10} />,
  },
  {
    title: <span className={headerCls}>Tags</span>,
    key: 'tags',
    render: (_, r) => (
      <span className="inline-flex flex-wrap gap-1">
        {(r.tags || []).map((t) => (
          <span key={t} className="rounded-full bg-slate-100 px-2 py-0.5 text-xs font-semibold text-slate-600 dark:bg-slate-700 dark:text-slate-300">
            {t}
          </span>
        ))}
      </span>
    ),
  },
  {
    // The list card carries no updatedate; the publish day is what readers see.
    title: <span className={headerCls}>Published</span>,
    key: 'publishedat',
    render: (_, r) => <span className="whitespace-nowrap">{formatPublishedAt(r.publishedat) || '—'}</span>,
  },
  {
    title: <span className={headerCls}>Published by</span>,
    key: 'publisher',
    render: (_, r) => <span className="whitespace-nowrap">{publisherName(r) || '—'}</span>,
  },
  {
    title: '',
    key: 'edit',
    align: 'right',
    render: () => (
      <span className="inline-flex items-center gap-1 text-sm font-semibold text-blue-600 dark:text-sky-400">
        Edit
        <ChevronRightIcon className="h-4 w-4" aria-hidden />
      </span>
    ),
  },
];

const ReleaseNotesAdmin = () => {
  const allowed = useCanManageReleaseNotes();
  const [rows, setRows] = useState([]);
  const [paged, setPaged] = useState(null);
  const [loading, setLoading] = useState(true);
  const [denied, setDenied] = useState(false);
  const [failed, setFailed] = useState(false);
  const [status, setStatus] = useState('all');
  const [search, setSearch] = useState('');
  const [debounced, setDebounced] = useState('');

  useEffect(() => {
    const t = setTimeout(() => setDebounced(search.trim()), SEARCH_DEBOUNCE_MS);
    return () => clearTimeout(t);
  }, [search]);

  const fetchRows = useCallback(
    async (pageNumber = 1) => {
      setLoading(true);
      try {
        const q = `?pageNumber=${pageNumber}&pageSize=${ADMIN_PAGE_SIZE}${debounced ? `&search=${encodeURIComponent(debounced)}` : ''}`;
        const res = await releaseNoteAdminApi.list(q);
        setRows(res.data?.data || []);
        setPaged(res.data || null);
        setFailed(false);
      } catch (err) {
        if (isForbidden(err)) setDenied(true);
        else setFailed(true); // a network error must not read as "No notes yet"
        message.error(apiErrorText(err, "Couldn't load release notes"));
        setRows([]);
      } finally {
        setLoading(false);
      }
    },
    [debounced],
  );

  useEffect(() => {
    if (allowed) fetchRows(1);
  }, [allowed, fetchRows]);

  const visible = useMemo(
    () => rows.filter((r) => (status === 'all' ? true : status === 'draft' ? r.status === 0 : r.status === 10)),
    [rows, status],
  );

  if (!allowed || denied) return <AccessDenied />;

  // A link that looks like TWButton's primary variant: a button nested in a
  // link is two controls to assistive tech, and the navigation is the action.
  const newNote = (
    <Link
      to={`${ADMIN_PATH}/new`}
      className="inline-flex h-9 items-center justify-center gap-1.5 rounded-md border border-transparent bg-blue-600 px-4 text-xs font-semibold text-white shadow-sm transition hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-1 dark:bg-sky-500 dark:text-slate-900 dark:hover:bg-sky-400"
    >
      <PlusIcon className="h-3.5 w-3.5" aria-hidden />
      New note
    </Link>
  );

  return (
    <div className={ADMIN_SHELL_CLASS}>
      <div className="mx-auto max-w-6xl space-y-6">
        <PageHeader
          title="Release Notes"
          description="Write and publish product updates. Published notes appear under What's new for every dashboard user."
          actions={newNote}
        />

        <div className="flex flex-wrap items-center justify-between gap-3">
          <SegmentedToggle
            ariaLabel="Status"
            value={status}
            onChange={setStatus}
            options={[
              { value: 'all', label: 'All' },
              { value: 'draft', label: 'Drafts' },
              { value: 'published', label: 'Published' },
            ]}
          />
          <div className="w-full sm:max-w-xs">
            <TWInput
              size="md"
              leftIcon={MagnifyingGlassIcon}
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              placeholder="Search by title"
              aria-label="Search by title"
            />
          </div>
        </div>

        <TWDataTable
          columns={columns}
          data={visible}
          rowKey="id"
          loading={loading}
          rowClickPath={(r) => `${ADMIN_PATH}/${r.id}`}
          // While the request is in flight antd paints the empty text under the
          // spinner; say nothing then, or every load flashes "No notes yet".
          empty={
            loading ? (
              <span />
            ) : failed ? (
              <EmptyState
                variant="plain"
                title="Couldn't load release notes"
                description="Check your connection and try again."
                action={<TWButton variant="primary" label="Retry" onClick={() => fetchRows(1)} />}
              />
            ) : (
              <EmptyState
                variant="plain"
                title={debounced || status !== 'all' ? 'No notes match' : 'No notes yet'}
                description={debounced || status !== 'all' ? 'Try another filter.' : 'Write the first update.'}
                action={debounced || status !== 'all' ? null : newNote}
              />
            )
          }
          pagination={
            paged && paged.totalRecords > ADMIN_PAGE_SIZE
              ? {
                  current: paged.pageNumber,
                  pageSize: ADMIN_PAGE_SIZE,
                  total: paged.totalRecords,
                  onChange: (page) => fetchRows(page),
                }
              : undefined
          }
        />
      </div>
    </div>
  );
};

export default ReleaseNotesAdmin;
